import React, { useMemo, useState } from "react";
import {
  AppBar,
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  TextField,
  Toolbar,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { ReactComponent as Menu } from "assets/Navbar/Menu.svg";
import { ReactComponent as Logout } from "assets/Navbar/logout-ico.svg";
import { ReactComponent as Language } from "assets/Modal/Group.svg";
import { useSelector, useDispatch } from "react-redux";
import { selectUserInfo, clearUserInfo } from "features/user/userSlice";
import { clearSubscriptionStatuses } from "features/subscription/subscriptionSlice";
import DropdownMenu from "common/DropDownMenu/DropDownMenu";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthLogoutService } from "subModule/src/services/userProfile/useProfile";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import AlertMessages from "common/Alert/AlertMessages";
import { brandingInfo } from "features/server/brandingSlice";
import { themes } from "theme/themes";
import ButtonNew from "common/Button/Button";
import Notifications from "components/Notifications";
import { checkSubscriptions } from "Helpers/CheckSubscriptions";

interface NavigationItem {
  checked: boolean;
  href?: string;
  icon?: React.ReactNode;
  title: string;
  tooltip?: string;
  subItems?: Record<string, NavigationItem>;
}

interface ModuleOption {
  id: string;
  label: string;
  group: string;
  href: string;
  titleKey: string;
}

interface NavbarProps {
  onMenuClick: () => void;
  handleLanguageSelect: (lang: string) => void;
  stackTopOffset?: number;
  navigation?: Record<string, NavigationItem>;
}

const flattenModules = (
  navigation: Record<string, NavigationItem> | undefined,
  t: (key: string) => string,
): ModuleOption[] => {
  if (!navigation || Array.isArray(navigation)) return [];

  const options: ModuleOption[] = [];

  Object.entries(navigation).forEach(([parentKey, item]) => {
    if (!item?.checked) return;

    const parentLabel = t(item.title || parentKey);

    if (item.href) {
      options.push({
        id: parentKey,
        label: parentLabel,
        group: parentLabel,
        href: item.href,
        titleKey: item.title,
      });
    }

    if (item.subItems) {
      Object.entries(item.subItems).forEach(([subKey, subItem]) => {
        if (!subItem?.checked || !subItem.href) return;
        options.push({
          id: `${parentKey}.${subKey}`,
          label: t(subItem.title),
          group: parentLabel,
          href: subItem.href,
          titleKey: subItem.title,
        });
      });
    }
  });

  return options.sort((a, b) => {
    const groupCmp = a.group.localeCompare(b.group);
    return groupCmp !== 0 ? groupCmp : a.label.localeCompare(b.label);
  });
};

const filterModulesByQuery = (options: ModuleOption[], query: string) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return options;
  return options.filter(
    (option) =>
      option.label.toLowerCase().includes(normalizedQuery) ||
      option.group.toLowerCase().includes(normalizedQuery),
  );
};

const Navbar: React.FC<NavbarProps> = ({
  onMenuClick,
  handleLanguageSelect,
  stackTopOffset = 0,
  navigation,
}) => {
  const { t } = useTranslation();
  const loggedInUserData = useSelector(selectUserInfo);
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const navigate = useNavigate();
  const location = useLocation();
  const isMarketplaceOpen = location.pathname.startsWith("/marketplace");
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [lang, setLang] = useState<null | HTMLElement>(null);
  const [moduleSearch, setModuleSearch] = useState<ModuleOption | null>(null);
  const [moduleInput, setModuleInput] = useState("");

  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const moduleOptions = useMemo(
    () => flattenModules(navigation, t),
    [navigation, t],
  );

  const handleModuleSelect = (option: ModuleOption | null) => {
    if (!option?.href) return;
    localStorage.setItem("selectedSubItem", option.titleKey);
    navigate(option.href);
    setModuleSearch(null);
    setModuleInput("");
  };

  const handleModuleSearchEnter = () => {
    const matches = filterModulesByQuery(moduleOptions, moduleInput);
    if (matches.length > 0) handleModuleSelect(matches[0]);
  };

  const handleClickLang = (event: React.MouseEvent<HTMLElement>) => {
    setLang(event.currentTarget);
  };

  const handleClose = () => {
    setLang(null);
  };

  const { onLogOut } = useAuthLogoutService({
    onSuccess: (response) => {
      if (response.success) {
        dispatch(clearUserInfo());
        dispatch(clearSubscriptionStatuses());
        [
          "userInfo",
          "selectedSubItem",
          "ACCESS_TOKEN",
          "loginAsOwnerToken",
          "loginAsOwnerUserInfo",
        ].forEach((item) => localStorage.removeItem(item));
        navigate("/login");
      } else {
        enqueueSnackbar(<AlertMessages statusCode={response.status} />, {
          variant: "error",
        });
      }
    },
    onError: (err) => {
      console.error("Login error======", err);
      if (err instanceof Error) {
        console.error(err.stack);
      }
    },
  });

  return (
    <AppBar
      position="fixed"
      className="hide-on-print"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        boxShadow: "none",
        color: "black",
        ...(stackTopOffset ? { top: stackTopOffset } : {}),
      }}
    >
      <Toolbar className="bg-layoutbg-color">
        <div style={{ display: "flex", flex: 1, alignItems: "center" }}>
          <Box sx={{ "& rect": { fill: themeColors?.primary } }}>
            <Menu
              onClick={onMenuClick}
              className="focus:outline-none transform transition duration-100 hover:scale-95"
              style={{ margin: "15px 18px 16px 0px", cursor: "pointer" }}
              width={36}
              height={36}
            />
          </Box>
          {branding?.attributes?.logo?.startsWith("blob:") ||
          branding?.attributes?.logo?.startsWith("data:") ||
          branding?.attributes?.logo?.startsWith("http") ? (
            <img
              src={branding.attributes.logo}
              alt=""
              className="mx-[8px]"
              style={{
                width: "140px",
                height: "67px",
                maxHeight: "80px",
                objectFit: "contain",
              }}
            />
          ) : null}
          <div className="flex flex-1 justify-between py-2 items-center gap-3">
            <Box
              className="flex items-center justify-start flex-1 min-w-0"
              sx={{ maxWidth: { xs: 180, sm: 280, md: 360 } }}
            >
              <Autocomplete
                fullWidth
                size="small"
                autoHighlight
                options={moduleOptions}
                groupBy={(option) => option.group}
                value={moduleSearch}
                inputValue={moduleInput}
                onChange={(_, value) => handleModuleSelect(value)}
                onInputChange={(_, value, reason) => {
                  setModuleInput(value);
                  if (reason === "clear") {
                    setModuleSearch(null);
                    setModuleInput("");
                  }
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options, { inputValue }) =>
                  filterModulesByQuery(options, inputValue)
                }
                noOptionsText={t("noModulesFound")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t("searchModules")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        if (
                          e.currentTarget.getAttribute("aria-expanded") ===
                          "true"
                        ) {
                          return;
                        }
                        e.preventDefault();
                        handleModuleSearchEnter();
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon
                              sx={{ color: themeColors?.primary, fontSize: 19 }}
                            />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: 40,
                        borderRadius: "10px",
                        backgroundColor: "#fff",
                        fontFamily: "Saira, sans-serif",
                        fontSize: 13,
                        "& fieldset": {
                          borderColor: "rgba(0,0,0,0.12)",
                        },
                        "&:hover fieldset": {
                          borderColor: themeColors?.primary,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: themeColors?.primary,
                          borderWidth: 1.5,
                        },
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box className="font-Saira text-[12px] py-0.5">
                      {option.label}
                    </Box>
                  </li>
                )}
                componentsProps={{
                  paper: {
                    sx: {
                      fontFamily: "Saira, sans-serif",
                      "& .MuiAutocomplete-groupLabel": {
                        fontFamily: "Saira, sans-serif",
                        fontWeight: 600,
                        fontSize: 11,
                        color: themeColors?.primary,
                        backgroundColor: "#f7f7f7",
                      },
                    },
                  },
                }}
              />
            </Box>
            <div className="flex justify-between shrink-0 items-center">
              <Tooltip title={t("marketplace")}>
                <Box
                  onClick={() => navigate("/marketplace")}
                  className="flex items-center gap-1.5 h-10 px-3 mr-3 cursor-pointer rounded-[10px] font-Saira font-semibold text-[13px]"
                  sx={{
                    background: isMarketplaceOpen
                      ? themeColors?.secondary || "#FC5F3A"
                      : `${themeColors?.primary || "#4C0055"}18`,
                    color: isMarketplaceOpen
                      ? "#fff"
                      : themeColors?.primary || "#4C0055",
                    "&:hover": {
                      opacity: 0.92,
                    },
                  }}
                >
                  <StorefrontOutlinedIcon
                    sx={{
                      fontSize: 19,
                      color: isMarketplaceOpen
                        ? "#fff"
                        : themeColors?.primary || "#4C0055",
                    }}
                  />
                  {/* <Box
                    component="span"
                    sx={{ display: { xs: "none", sm: "inline" } }}
                  >
                    {t("marketplace")}
                  </Box> */}
                </Box>
              </Tooltip>
              {checkSubscriptions("NOTIFICATIONS") && <Notifications />}
              <Box
                className="border-r-2 flex h-10 pl-4 pr-2 items-center justify-start mr-4"
                style={{ borderColor: themeColors?.primary }}
              >
                <Box
                  className="font-Saira font-[500] text-txt-color text-ellipsis"
                  sx={{
                    fontSize: { xs: "13px", md: "16px" },
                  }}
                >
                  {loggedInUserData?.username || ""}
                </Box>
              </Box>
              <Box className="flex gap-1">
                <Tooltip title={t("Change Language")}>
                  <span
                    className="h-10 cursor-pointer rounded-[10px] w-10 flex text-center items-center justify-center"
                    style={{ background: themeColors?.button }}
                    onClick={handleClickLang}
                  >
                    {<Language />}
                  </span>
                </Tooltip>
                {/* <Tooltip title={t("Account Settings")}>
                  <span
                    className="h-10 cursor-pointer rounded-[10px] w-10 flex text-center items-center justify-center"
                    onClick={() => navigate("/account-settings/change-password")}
                  >
                    <NavUser
                      height={"40px"}
                      width={"40px"}
                      style={{
                        borderRadius: "10px",
                        borderWidth: "3px",
                        borderColor: themeColors?.primary,
                      }}
                    />
                  </span>
                </Tooltip> */}
                <Tooltip title={t("Log Out")}>
                  <Box
                    sx={{ "& rect": { fill: themeColors?.primary } }}
                    className="ml-2"
                  >
                    <Logout
                      onClick={() => setLogoutDialogOpen(true)} // Open confirmation dialog
                      style={{
                        borderRadius: "10px",
                        borderWidth: "3px",
                        borderColor: themeColors?.primary,
                        color: themeColors?.primary,
                        background: themeColors?.button,
                      }}
                      height={"40px"}
                      width={"40px"}
                    />
                  </Box>
                </Tooltip>
              </Box>
              {/* Logout Confirmation Dialog */}
              <Dialog
                open={logoutDialogOpen}
                onClose={() => setLogoutDialogOpen(false)} // Close dialog on cancel
                aria-labelledby="logout-dialog-title"
                aria-describedby="logout-dialog-description"
              >
                <DialogTitle
                  id="logout-dialog-title"
                  className="font-Saira font-bold text-[17px]"
                >
                  {t("Confirm Logout")}
                </DialogTitle>
                <DialogContent>
                  <DialogContentText
                    id="logout-dialog-description"
                    className="font-Saira"
                  >
                    {t("Are you sure you want to log out?")}
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <ButtonNew
                    onClick={() => setLogoutDialogOpen(false)}
                    // color="primary"
                    type="back"
                    text={t("Cancel")}
                  >
                    {/* {t("Cancel")} */}
                  </ButtonNew>
                  <ButtonNew
                    onClick={() => {
                      setLogoutDialogOpen(false);
                      onLogOut(); // Call the logout function
                    }}
                    type="create"
                    text={t("Log Out")}
                    // type={"back"}
                  ></ButtonNew>
                </DialogActions>
              </Dialog>
            </div>
          </div>
          <DropdownMenu
            anchorEl={lang}
            langItems={[
              { label: "English", onClick: () => handleLanguageSelect("en") },
              { label: "عربي", onClick: () => handleLanguageSelect("ar") },
              { label: "اردو", onClick: () => handleLanguageSelect("ur") },
              { label: "Français", onClick: () => handleLanguageSelect("fr") },
            ]}
            onClose={handleClose}
            hoverColor={themeColors?.primary}
          />
        </div>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
